import { Activity, AlertTriangle, FlaskConical, Info, Waves } from "lucide-react";
import type { BodyLoadResult, BodyLoadGroup } from "@/domain/pkBodyLoadEngine";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

function formatAmount(value: number, unit: string) {
  const abs = Math.abs(value);
  const digits = unit === "µg" || unit === "ng" ? (abs < 10 ? 1 : 0) : abs < 1 ? 2 : abs < 10 ? 1 : 0;
  return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: digits }).format(value);
}

function uncertaintyText(group: BodyLoadGroup) {
  if (!(group.remainingHigh > group.remainingLow)) return `≈ ${formatAmount(group.remainingMid, group.unit)} ${group.unit}`;
  const spread = (group.remainingHigh - group.remainingLow) / Math.max(group.remainingMid, 1e-9);
  if (spread < 0.08) return `≈ ${formatAmount(group.remainingMid, group.unit)} ${group.unit}`;
  return `${formatAmount(group.remainingLow, group.unit)}–${formatAmount(group.remainingHigh, group.unit)} ${group.unit}`;
}

function sourceLabel(group: BodyLoadGroup) {
  if (group.sources.length === 1 && group.sources[0] === "formulation") return "formulação";
  if (group.sources.includes("formulation")) return "fontes mistas";
  if (group.sources.includes("canonical")) return "canônico";
  return "biblioteca";
}

function confidenceVariant(confidence: BodyLoadGroup["confidence"]): "default" | "secondary" | "outline" {
  if (confidence === "alta") return "default";
  if (confidence === "moderada") return "secondary";
  return "outline";
}

export function BodyLoadNowCard({ result, historyWindowDays = 120 }: { result: BodyLoadResult; historyWindowDays?: number }) {
  return (
    <Card className="rounded-3xl p-4 shadow-sm sm:p-5">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-cyan-500/10 text-cyan-500">
          <Waves className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold">No corpo agora</h2>
            <Badge variant="outline">estimativa PK</Badge>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Quantidade parent-equivalente ainda não eliminada segundo meia-vida e, quando disponível, absorção farmacocinética. Não representa concentração sérica nem intensidade de efeito.
          </p>
        </div>
      </div>

      {result.groups.length ? (
        <div className="mt-4 space-y-2.5">
          {result.groups.slice(0, 10).map((group) => {
            const percent = Math.max(0, Math.min(100, group.remainingFraction * 100));
            return (
              <div key={group.key} className="rounded-2xl border bg-muted/15 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="break-words text-sm font-medium">{group.canonicalName}</div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {group.modeledDoseCount} dose(s) modelada(s) · parâmetros: {sourceLabel(group)}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-sm font-semibold tabular-nums">{uncertaintyText(group)}</div>
                    <Badge variant={confidenceVariant(group.confidence)} className="mt-1 text-[9px]">confiança {group.confidence}</Badge>
                  </div>
                </div>
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-cyan-500 transition-[width]" style={{ width: `${percent}%` }} />
                </div>
                <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                  <span>≈ {percent.toFixed(0)}% das doses modeladas ainda não eliminado</span>
                  <span>{group.models.includes("first_order_absorption") ? "absorção + eliminação" : "envelope de eliminação"}</span>
                </div>
                {group.notes.length ? (
                  <div className="mt-2 space-y-1 border-t pt-2 text-[10px] text-muted-foreground">
                    {group.notes.slice(0, 2).map((note) => <div key={note} className="flex gap-1.5"><Info className="mt-0.5 h-3 w-3 shrink-0" /><span>{note}</span></div>)}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-4 rounded-2xl border border-dashed p-4 text-sm text-muted-foreground">
          <div className="flex gap-2"><FlaskConical className="mt-0.5 h-4 w-4 shrink-0" /><span>Sem doses recentes com meia-vida estruturada suficiente para estimar quantidade residual.</span></div>
        </div>
      )}

      <div className="mt-4 space-y-1.5 border-t pt-3 text-[10px] leading-relaxed text-muted-foreground">
        <div className="flex gap-1.5"><Activity className="mt-0.5 h-3 w-3 shrink-0" /><span>O cálculo usa o histórico disponível dos últimos {historyWindowDays} dias. Esquemas de acúmulo muito prolongado podem ser subestimados.</span></div>
        <div className="flex gap-1.5"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /><span>Metabólitos ativos, tolerância e resposta farmacodinâmica são dimensões separadas e não são convertidos automaticamente em “mg no corpo”.</span></div>
        {result.skippedDoseCount > 0 ? <div>{result.skippedDoseCount} dose(s) não entraram na estimativa por dados PK insuficientes ou incompatíveis.</div> : null}
      </div>
    </Card>
  );
}
